import { Classes, Dialog, Intent, Radio } from '@blueprintjs/core';
import classNames from 'classnames';
import Site from 'es/app/business-logic/domain-models/Site';
import flushCache from 'es/app/business-logic/services/diagnostics/flushCache';
import { ActionButton } from 'es/app/components/buttons/Buttons';
import { DropDownControl } from 'es/app/components/forms/components';
import Throbber from 'es/app/components/progress-indicators/Throbber';
import { useUserStore } from 'es/app/store/GlobalStore';
import { BootstrapStyles } from 'es/app/visuals/styles/BootstrapStyles';
import { useI18n } from 'es/core/hooks/useI18n';
import usePromiseTracker from 'es/core/hooks/usePromiseTracker';
import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useState } from 'react';
import { usePromise } from 'react-use';

interface IProps {
    show: boolean;
    onClose: () => void;
}

const FlushCacheDialog = (props: IProps) => {
    const { l10n } = useI18n(['app/components', 'app']);

    const store = useUserStore();

    const [site, setSite] = useState(store.activeSite);
    useEffect(() => setSite(store.activeSite), [store.activeSite]);

    const [flushGlobal, setFlushGlobal] = useState(false);
    const [flushCount, setFlushCount] = useState<number>(-1);

    const hasFlushed = flushCount !== -1;
    const resultMessage =
        flushCount >= 0
            ? l10n('flushCacheDialog.activity.flushSuccessful', { count: flushCount })
            : l10n('flushCacheDialog.activity.flushFailed');

    const resolveIfMounted = usePromise();
    const { isBusy, status, trackPromise } = usePromiseTracker();

    const facilities = useMemo(() => {
        return store.accessibleFacilities?.map(facility => ({
            label: `${facility.companyName} - ${facility.name} (${facility.site.toString()})`,
            value: facility.site,
        }));
    }, [store.accessibleFacilities]);

    return (
        <Dialog
            isOpen={props.show}
            canEscapeKeyClose={true}
            canOutsideClickClose={true}
            onClose={handleClose}
            className="flush-cache-dialog"
        >
            <div className={Classes.DIALOG_HEADER}>
                <h5>{l10n('flushCacheDialog.title')}</h5>
            </div>

            <div className={Classes.DIALOG_BODY}>
                <Throbber visible={isBusy} text={status} />

                {!hasFlushed && (
                    <>
                        <div
                            className={classNames(
                                BootstrapStyles['d-flex'],
                                BootstrapStyles['align-items-center'],
                                BootstrapStyles['mb-3']
                            )}
                        >
                            <Radio
                                radioGroup="cache"
                                label={l10n('flushCacheDialog.label.flushFacilityCache')}
                                checked={!flushGlobal}
                                onChange={handleFacilityChange}
                                className={BootstrapStyles['me-2']}
                            />

                            {site && (
                                <DropDownControl<{ label: string; value: Site }, Site>
                                    data={facilities ?? []}
                                    textField="label"
                                    valueField="value"
                                    value={site}
                                    onChange={handleChange}
                                    className={BootstrapStyles['flex-fill']}
                                    disabled={flushGlobal}
                                />
                            )}
                        </div>

                        <Radio
                            radioGroup="cache"
                            label={l10n('flushCacheDialog.label.flushGlobal')}
                            checked={flushGlobal}
                            onChange={handleGlobalChange}
                        />
                    </>
                )}

                {hasFlushed && resultMessage}
            </div>

            <div className={Classes.DIALOG_FOOTER}>
                {!hasFlushed && (
                    <>
                        <ActionButton intent={Intent.PRIMARY} text={l10n('app:action.ok')} onClick={handleFlushCache} />
                        <ActionButton text={l10n('app:action.cancel')} onClick={handleClose} />
                    </>
                )}
                {hasFlushed && (
                    <ActionButton
                        intent={flushCount >= 0 ? Intent.PRIMARY : Intent.DANGER}
                        text={l10n('app:action.close')}
                        onClick={handleClose}
                    />
                )}
            </div>
        </Dialog>
    );

    ////////////////////

    function handleGlobalChange(event: React.ChangeEvent<HTMLInputElement>) {
        setFlushGlobal(event.target.checked);
    }

    function handleFacilityChange(event: React.ChangeEvent<HTMLInputElement>) {
        setFlushGlobal(!event.target.checked);
    }

    function handleChange(site: Site) {
        setSite(site);
    }

    function handleFlushCache() {
        trackPromise(async () => {
            const flushSite = flushGlobal ? undefined : site;
            const result = await resolveIfMounted(flushCache(flushSite));

            setFlushCount(result);
        });
    }

    function handleClose() {
        props.onClose();

        setFlushGlobal(false);
        setFlushCount(-1);
    }
};

export default observer(FlushCacheDialog);
